<template>
    <div class="content" v-if="question">
        <div>
            <div class="backButton">
                <button @click="goToList"><i class="fas fa-arrow-left-long"></i> Back<span v-if="surveyUrl"> to Questions</span></button>
            </div>
            <div class="flexBreak"></div>
            <div v-if="surveyUrl" class="backButton backToSurvey">
                <button @click="goToSurvey"><i class="fas fa-arrow-left-long"></i> Back to Survey</button>
            </div>
            <div class="questionContainer">
                <div>
                    <h4>Description: </h4>
                    <textarea type="text" v-model="question.description" :disabled="!question.isActive" @change="saveQuestion('description')"></textarea>
                </div>
                <div>
                    <div style="display: flex; flex-direction: row; justify-content: space-between;">
                        <h4>Text: </h4>
                        <TranslatedText v-if="question.languages && question.languages.length > 1"
                            :textJson="question.questionTextJson" :defaultLanguage="question.defaultLanguage"></TranslatedText>
                    </div>
                    <textarea type="text" v-model="questionText" :disabled="!question.isActive" @change="saveQuestion('questionTextJson')"></textarea>
                    <p>
                        <span v-if="questionText">{{questionText.length}}</span>
                        <span v-if="!questionText">0</span>
                        /2000 Characters
                    </p>
                </div>
                <div class="dataRow" v-if="question.languages">
                    <LanguageManagement :objectLanguageArray="question.languages" :defaultLanguage="question.defaultLanguage"
                        :onLanguageAdd="addLanguage" :onDefaultChange="changeDefaultLanguage" :onLanguageDelete="deleteLanguage"></LanguageManagement>
                </div>
                <div class="dataRow">
                    <h4>Attribute: </h4>
                    <select v-model="question.attributeId" @blur="saveQuestion('attributeId')" :disabled="!question.isActive">
                        <option v-for="attribute in attributes" :key="attribute.id" :value="attribute.id">{{attribute.code}}</option>
                    </select>
                </div>
                <div class="dataRow">
                    <h4>Question Type: </h4>
                    <span>{{question.questionType}}</span>
                </div>
                <div class="dataRow">
                    <h4>Answer Type: </h4>
                    <span>{{question.answerType}}</span>
                </div>
                <div class="dataRow"  v-if="question.questionType === 'SingleSelect' || question.questionType === 'MultiSelect' || question.questionType === 'Combination'">
                    <h4>Answer Type: </h4>
                    <select v-model="question.answerType" @blur="saveQuestion('answerType')" :disabled="!question.isActive">
                        <option v-for="at in answerTypes" :key="at.value" :value="at.internalName">{{at.displayName}}</option>
                    </select>
                </div>
                <div v-if="question.questionType === 'SingleSelect' || question.questionType === 'MultiSelect'" class="dataRow">
                    <h4>How Many Columns of Answers?</h4>
                    <input type="number" min="1" max="5" step="1" v-model="question.displayParameters.columns" :disabled="!question.isActive" @change="saveQuestion('displayParameters')" />
                </div>
                <template v-if="question.answerType === 'Text'">
                    <div class="dataRow">
                        <div><h4 style="margin: 0px">Maximum Length</h4><br/>(enter 0 for no maximum)</div>
                        <input type="number" min="0" step="1" v-model="question.displayParameters.maxLength" :disabled="!question.isActive" @change="saveQuestion('displayParameters')" />
                    </div>
                    <div class="dataRow">
                        <div><h4 style="margin: 0px">Maximum Rows</h4><br/>(enter 0 for no maximum)</div>
                        <input type="number" min="0" step="1" v-model="question.displayParameters.maxRows" :disabled="!question.isActive" @change="saveQuestion('displayParameters')" />
                    </div>
                    <div class="dataRow">
                        <div><h4 style="margin: 0px">Maximum Length of Each Row</h4><br/>(enter 0 for no maximum)</div>
                        <input type="number" min="0" step="1" v-model="question.displayParameters.maxRowLength" :disabled="!question.isActive" @change="saveQuestion('displayParameters')" />
                    </div>
                </template>
                <template v-if="question.answerType === 'Numeric'">
                    <div class="dataRow">
                        <div><h4 style="margin: 0px">Set Minimum Value?</h4></div>
                        <input type="checkbox" v-model="question.setMinValue" @change="toggleParam($event, 'minValue')"/>
                        <input type="number" :style="{visibility: question.setMinValue ? 'visible' : 'hidden'}"
                                            v-model="question.displayParameters.minValue" 
                                            :disabled="!question.isActive" 
                                            @change="saveQuestion('displayParameters')" />
                    </div>
                    <div class="dataRow">
                        <div><h4 style="margin: 0px">Set Maximum Value?</h4></div>
                        <input type="checkbox" v-model="question.setMaxValue" @change="toggleParam($event, 'maxValue')" />
                        <input type="number" :style="{visibility: question.setMaxValue ? 'visible' : 'hidden'}" 
                                            v-model="question.displayParameters.maxValue" 
                                            :disabled="!question.isActive"
                                            @change="saveQuestion('displayParameters')" />
                    </div>
                    <div class="dataRow">
                        <div><h4 style="margin: 0px">Step</h4><br/>(defaults to 1)</div>
                        <input type="number" min="0"
                                            v-model="question.displayParameters.numberStep"
                                            :disabled="!question.isActive"
                                            @change="saveQuestion('displayParameters')" />
                    </div>
                </template>
                <template v-if="question.answerType === 'Text' || question.answerType === 'Numeric'">
                    <div class="dataRow">
                        <div><h4 style="margin: 0px">Pattern</h4><br/>(regex)</div>
                        <input type="text" v-model="question.displayParameters.pattern"
                                            :disabled="!question.isActive"
                                            @change="saveQuestion('displayParameters')" />
                    </div>
                </template>
                <template v-if="hasPlaceholder(question)">
                    <div class="dataRow" style="margin-top: 10px;">
                        <div>
                            <h4 style="margin: 0px;">Placeholder Text</h4>
                        </div>
                        <div>
                            <input type="text" v-model="question.displayParameters.placeholder" :disabled="!question.isActive" @change="saveQuestion('displayParameters')" />
                            <div style="display: flex; flex-direction: row; justify-content: space-between;">
                                <div></div>
                                <TranslatedText v-if="question.languages && question.languages.length > 1"
                                    :textJson="question.displayParameters.placeholderJson"
                                    position="bottom" alignment="start" :defaultLanguage="question.defaultLanguage"></TranslatedText>
                            </div>
                        </div>
                    </div>
                </template>
                <div class="dataRow">
                    <h4>Status:</h4>
                    <h4>
                        <span v-if="question.isActive">Active</span>
                        <span v-if="!question.isActive">Inactive</span>
                    </h4>
                </div>
                <div>
                    <div style="display: flex; flex-direction: row; justify-content: space-between;">
                        <h4>More Info: </h4>
                        <TranslatedText v-if="question.moreInfo && question.languages && question.languages.length > 1"
                                    :textJson="question.moreInfoJson"
                                    position="top" :defaultLanguage="question.defaultLanguage"></TranslatedText>
                    </div>
                    <textarea type="text" v-model="moreInfo" :disabled="!question.isActive" @change="saveQuestion('moreInfoJson')"></textarea>
                    <p>
                        <span v-if="moreInfo">{{moreInfo.length}}</span>
                        <span v-if="!moreInfo">0</span>
                        /500 Characters
                    </p>
                </div>
                <div v-if="question.questionType != 'MediaDisplay'">
                    <h4>ToolTip: </h4>
                    <select v-model="question.mediaPropertyId" @blur="saveQuestion('mediaPropertyId')" :disabled="!question.isActive">
                        <option :value="0">-- No ToolTip --</option>
                        <option v-for="tt in tooltips" :key="tt.id" :value="tt.id">{{ tt.description }}</option>
                    </select>
                </div>
                <button class="reactivateButton" v-if="!question.isActive" @click="reactivateQuestion">Reactivate Question</button>
                <button class="deactivateButton" v-if="question.isActive" @click="deactivateQuestion">Deactivate Question</button>
                <p v-if="question.questionType === 'SingleSelect' || question.questionType === 'MultiSelect'">Show deleted elements <input type="checkbox" v-model="showDeletedElements" @change="getQuestion" /></p>
                <FKLookupListing v-if="question.id > 0" recordType="surveys" editPage="surveyEdit" :onLoadData="getLinkedSurveys"></FKLookupListing>
            </div>
        </div>
        <div v-if="isSelectQuestion || isValueSelectQuestion" class="answersContainer">
            <div v-if="isValueSelectQuestion" style="display:flex; flex-direction: column; width: 400px;">
                <h2>Label Display Type: </h2>
                <select v-model="question.displayParameters.labelDisplayType" @blur="saveQuestion('displayParameters')" 
                        :disabled="!question.isActive" style="width:200px">
                    <option v-for="answerType in answerTypes" :key="answerType.value" :value="answerType.internalName">{{answerType.displayName}}</option>
                </select>
                <h2>Only Allow Numbers in Textbox: </h2>
                <input type="checkbox" v-model="question.displayParameters.allowNumbersOnly" :disabled="!question.isActive" @change="saveQuestion('displayParameters')" />
            </div>        
            <h2 v-if="isSelectQuestion">Answers</h2>
            <h2 v-if="isValueSelectQuestion">Labels</h2>
            <div class="row row-header">
                <div class="grip"></div>
                <div class="column-2x" style="font-weight:bold">
                    {{  isSelectQuestion ? "Answer" : "Label"}} Text
                </div>
                <div class="column-1x">
                    {{  isSelectQuestion ? "Answer" : "Label"}} Value
                </div>
                <div class="column-1x">
                    Display Type
                </div>
                <div class="column-1x conditionalJson">Display Conditions<br >
                (processed on client)</div>
                <div class="button-column">
                </div>
            </div>
            <VueDraggableNext class="dragArea list-group w-full" :list="question.answers" @update="moveAnswer">
                <template  v-for="(answer, index) in question.answers" :key="answer.id">
                <div class="row row-data" v-if="answer.isActive || this.showDeletedElements">
                    <div class="grip">
                        <i class="fa-solid fa-grip-vertical"></i>
                    </div>
                    <div class="column-2x">
                        <input type="text" style="width:90%" v-model="answer.answerText" :disabled="!question.isActive" @change="saveAnswer(answer, 'answerText')" />
                        <div style="display: flex; flex-direction: row; justify-content: space-between;">
                            <p>
                                <span v-if="answer.answerText">{{answer.answerText.length}}</span>
                                <span v-if="!answer.answerText">0</span>
                                /200 Characters
                            </p>
                            <TranslatedText v-if="question.languages && question.languages.length > 1"
                                    :textJson="answer.answerTextJson"
                                    position="bottom" alignment="start" :defaultLanguage="question.defaultLanguage"></TranslatedText>
                        </div>
                        <span style="color:red; font-weight: bold" v-if="!question.isActive || !answer.isActive">INACTIVE</span>
                    </div>
                    <div class="column-1x">
                        <input type="text" style="width:90%" v-model="answer.answerValue" :disabled="!question.isActive" @change="saveAnswer(answer, 'answerValue')" />
                        <p>
                            <span v-if="answer.answerValue">{{answer.answerValue.length}}</span>
                            <span v-if="!answer.answerValue">0</span>
                            /100 Characters
                        </p>
                    </div>
                    <div class="column-1x">
                        <select v-model="answer.type" @blur="saveAnswer(answer, 'type')" 
                                :disabled="!question.isActive" style="width: 90%">
                            <option v-for="type in displayTypes" :key="type.value" :value="type.internalName">{{type.displayName}}</option>
                        </select>
                    </div>
                    <div class="conditionalJson column-1x">
                        <div class="detailHeader" style="font-weight: bold; width: unset; display: flex; padding-right: 10px; align-items: baseline;flex-direction: row;">
                            <i @click="showConditionalModal(answer, index)" class="fa-pen-to-square fas fa-lg editFieldButton"></i>
                        </div>
                        <div class="conditionalJsonText" v-html="getPrintConditions(answer.conditionalJson)"></div>
                    </div>
                    <div class="button-column delete-button-column" v-if="answer.isActive">
                        <div  @click="deleteAnswer(answer)" v-if="question.isActive">
                            <i class="fas fa-lg fa-trash-can"></i>
                        </div>
                    </div>
                    <div class="button-column undelete-button-column" v-if="answer.isActive === false">
                        <div  @click="undeleteAnswer(answer)" v-if="question.isActive">
                            <i class="fa-sharp fa-solid fa-lg fa-trash-undo"></i>
                        </div>
                    </div>

                </div>
                </template>
            </VueDraggableNext>
            <ItemModal v-if="isSelectQuestion" :itemName="'Answer'"
                                :formRows="newAnswerFormRows"
                                :saveItemFunction="saveAnswer"
                                :refreshItemsFunction="getQuestion"
                                :isActive="question.isActive"></ItemModal>
            <ItemModal v-if="isValueSelectQuestion" :itemName="'Label'"
                                :formRows="newAnswerFormRows"
                                :saveItemFunction="saveAnswer"
                                :refreshItemsFunction="getQuestion"
                                :isActive="question.isActive"></ItemModal>
        </div>
        <div v-if="question.questionType === 'MediaDisplay'" class="mediaDisplayContainer">
            <h4>Image/Video:</h4>
            <button @click="showAddMediaPropertyModal()" class="openModalButton" :disabled="!question.isActive">
                <i v-if="question.mediaPropertyId === 0" class="fa-circle-plus fas fa-2xl"></i>
                <i v-if="question.mediaPropertyId > 0" class="fa-pen-to-square fas fa-2xl"></i>
            </button>
            <button v-if="question.mediaPropertyId > 0" @click="removeMediaProperty()" class="removeMediaPropertyButton" :disabled="!question.isActive">
                <i class="fa-trash-can fas fa-2xl"></i>
            </button>
            <AddMediaPropertyModal v-if="showAddVideoModal" @hideAddMediaPropertyModal="hideAddMediaPropertyModal" :object="question" 
                :isActive="question.isActive" :mediaTypes="mediaTypes" @saveMediaProperty="saveMediaProperty" :mediaTypeFilters="['Video', 'Image']"></AddMediaPropertyModal>
            <div v-if="question.mediaPropertyId > 0 && question.mediaPropertyInfo.mediaType != 'ToolTip'">
                <p>Type: {{ question.mediaPropertyInfo.mediaType }}</p>
                <p>Description: {{ question.mediaPropertyInfo.description }}</p>
            </div>
        </div>
        <div v-if="question.questionType === 'Combination' && question.answerType === 'TextboxAndButton'">
            <div class="answerValue">
                <p><b>Textbox Label:</b></p>
                <input type="text" v-model="ComboQuestionAnswers.textboxLabel" :disabled="!question.isActive" @change="saveComboAnswer(1, 'textboxLabel')" />
                <TranslatedText v-if="question.languages && question.languages.length > 1 && question.answers"
                                    :textJson="question.answers[0].answerTextJson"
                                    position="top" alignment="middle" :defaultLanguage="question.defaultLanguage"></TranslatedText>
                <p>
                    <span v-if="ComboQuestionAnswers.textboxLabel">{{ComboQuestionAnswers.textboxLabel.length}}</span>
                    <span v-if="!ComboQuestionAnswers.textboxLabel">0</span>
                    /100 Characters
                </p>
            </div>
            <div class="answerValue">
                <p><b>Button Text:</b></p>
                <input type="text" v-model="ComboQuestionAnswers.buttonText" :disabled="!question.isActive" @change="saveComboAnswer(2, 'buttonText')" />
                <TranslatedText v-if="question.languages && question.languages.length > 1 && question.answers && question.answers.length > 1"
                                    :textJson="question.answers[1].answerTextJson"
                                    position="top" alignment="middle" :defaultLanguage="question.defaultLanguage"></TranslatedText>
                <p>
                    <span v-if="ComboQuestionAnswers.buttonText">{{ComboQuestionAnswers.buttonText.length}}</span>
                    <span v-if="!ComboQuestionAnswers.buttonText">0</span>
                    /100 Characters
                </p>
            </div>
            <div class="answerValue">
                <p><b>Button Value:</b></p>
                <input type="text" v-model="ComboQuestionAnswers.buttonValue" :disabled="!question.isActive" @change="saveComboAnswer(2, 'buttonValue')" />
                <p>
                    <span v-if="ComboQuestionAnswers.buttonValue">{{ComboQuestionAnswers.buttonValue.length}}</span>
                    <span v-if="!ComboQuestionAnswers.buttonValue">0</span>
                    /100 Characters
                </p>
            </div>
        </div>
    </div>
    <ConditionalExpressionModal :conditions="conditionGroup" v-if="ExpressionModalVisible" @close="ExpressionModalVisible=false" 
            :onConditionsChanged="updateConditions"></ConditionalExpressionModal>
</template>

<script>
import {data} from '../../shared/data';
import {translation} from '../../shared/translation';
import ItemModal from '@/components/ItemModal.vue';
import FKLookupListing from '@/components/shared/FKLookupListing.vue';
import AddMediaPropertyModal from '@/components/mediaproperty/AddMediaPropertyModal.vue';
import {sorting} from '@/shared/sorting';
import { VueDraggableNext} from 'vue-draggable-next';
import { printConditions } from '@/shared/logicProcessing';
import ConditionalExpressionModal from '@/components/shared/ConditionalExpressions/ConditionalExpressionModal.vue';
import TranslatedText from '@/components/shared/TranslatedText.vue';
import LanguageManagement from '@/components/shared/LanguageManagement.vue';

export default {
    name: 'QuestionEdit',
    components: { ItemModal, FKLookupListing, AddMediaPropertyModal, VueDraggableNext, ConditionalExpressionModal, TranslatedText,
        LanguageManagement },
    data: function() {
        return {
            question: {},
            showDeletedElements: false,
            questionTypes: [],
            answerTypes: [],
            displayTypes: [],
            attributes: [],
            tooltips: [],
            surveyUrl: '',
            showAddVideoModal: false,
            cancelDrag: false,
            mediaTypes: [
                {
                displayName: 'Video',
                internalName: 'Video',
                value: 2
                },
                {
                displayName: 'Image',
                internalName: 'Image',
                value: 3
                },
                 ],
            placeholderTypes: [
                'Text',
                'SingleSelect',
                'MultiSelect',
                'Combination'
            ],
            ComboQuestionAnswers: {},
            newAnswerFormRows: [
                {
                    name: 'Answer Text',
                    propertyName: 'answerText',
                    required: true,
                    element: 'input',
                    maxLength: 200
                },
                {
                    name: 'Answer Value',
                    propertyName: 'answerValue',
                    required: true,
                    element: 'input',
                    maxLength: 100
                },
                {
                    name: 'Sequence',
                    propertyName: 'sequence',
                    required: true,
                    element: 'input'
                },
                {
                    name: 'Display Type',
                    propertyName: 'type',
                    required: true,
                    element: 'select'
                }

            ],
            ExpressionModalVisible: false,
            conditionalExpressionObject: null,
            ExpressionObjectType: '',
            conditionGroup: {},
        };
    },
    async mounted() {
        await this.getQuestion();
        this.getQuestionTypes();
        this.getAnswerTypes();
        this.getAttributes();
        this.getMediaProperties();
        this.getDisplayTypes();
        const urlParams = new URLSearchParams(window.location.search);
        this.surveyUrl = urlParams.get('return');
    },
    computed: {
        isSelectQuestion: function() {
            if(this.question.questionType === 'SingleSelect' || this.question.questionType === 'MultiSelect')
                return true;
            else
                return false;
        },
        isValueSelectQuestion: function() {
            if (this.question.questionType === 'Combination' && this.question.answerType === 'ValueSelectLabel')
                return true;
                else
                    return false;
        },
        visibleAnswers1: function() {
            return this.question.answers.filter(i => this.showDeletedElements || i.isActive);
        },
        questionText: {
            get() {
                return translation.getTextForLanguage(this.question.questionTextJson, this.question.defaultLanguage);
            },
            set(newText) {
                translation.setTextForLanguage(this.question, this.question.defaultLanguage, 'questionTextJson', newText);
            }
        },
        moreInfo: {
            get() {
                return translation.getTextForLanguage(this.question.moreInfoJson, this.question.defaultLanguage);
            },
            set(newText) {
                translation.setTextForLanguage(this.question, this.question.defaultLanguage, 'moreInfoJson', newText);
            }
        }
    },
    methods: {
        getQuestion: async function() {
            const urlParams = new URLSearchParams(window.location.search);
            var loadQuestion = await data.getQuestion(urlParams.get('ID'));
            if(loadQuestion.answers) {
                loadQuestion.answers.sort((a,b) => {
                    return a.sequence - b.sequence;
                });
            }

            if(loadQuestion.questionType == "Combination" && loadQuestion.answerType == "TextboxAndButton" &&
                loadQuestion.answers) {
                var answer = loadQuestion.answers.filter(search => search.sequence == 1);


                if(answer.length > 0) this.ComboQuestionAnswers.textboxLabel = this.getTextForLanguage(answer[0].answerTextJson, 'English');

                answer = loadQuestion.answers.filter(search => search.sequence == 2);

                if(answer.length > 0){
                    this.ComboQuestionAnswers.buttonText = this.getTextForLanguage(answer[0].answerTextJson, 'English');
                    this.ComboQuestionAnswers.buttonValue = answer[0].answerValue;     
                }
            }

            if(loadQuestion.displayParameters == null) {
                if(loadQuestion.questionType === 'SingleSelect' || loadQuestion.questionType === 'MultiSelect')
                        loadQuestion.displayParameters = { "columns": 2 };
                else if(loadQuestion.answerType === 'Text' && loadQuestion.questionType === 'Text')
                    loadQuestion.displayParameters = {
                        maxRows: 0,
                        maxLength: 0,
                        maxRowLength: 0
                    }
                
                if(this.hasPlaceholder(loadQuestion)) 
                    loadQuestion.displayParameters = { "placeholder": '', "placeholderJson": []};
            }

            if(loadQuestion.answerType === 'Numeric') {
                if(loadQuestion.displayParameters.minValue || loadQuestion.displayParameters.minValue === 0)
                    loadQuestion.setMinValue = true;
                if(loadQuestion.displayParameters.maxValue || loadQuestion.displayParameters.maxValue === 0)
                    loadQuestion.setMaxValue = true;
                if(loadQuestion.displayParameters.step)
                    loadQuestion.setStep = true;
            }
            
            if(this.isSelectQuestion || this.isValueSelectQuestion)
                loadQuestion.answers = loadQuestion.answers.filter(i => this.showDeletedElements || i.isActive);

            this.question = loadQuestion;
         },
        getQuestionTypes: async function() {
            var types = await data.getEnumList("QuestionTypes");
            this.questionTypes = sorting.sortArray(types, "displayName", "asc", 0, 0);  
        },
        getAnswerTypes: async function() {
            var answerList = await data.getEnumList(!this.isValueSelectQuestion ? "AnswerTypes" : "SelectionQuestionDisplayTypes");
            this.answerTypes = sorting.sortArray(answerList, "displayName", "asc", 0, 0);  
        },
        getDisplayTypes: async function() {
            var list = await data.getEnumList("AnswerDisplayTypes");
            this.displayTypes = sorting.sortArray(list, "displayName", "asc", 0, 0);  
            this.newAnswerFormRows.find(row => row.propertyName == 'type').selectOptions = this.displayTypes;

        },
        getAttributes: async function() {
            var attributeList =  await data.getAllAttributes('', true);
            this.attributes = sorting.sortArray(attributeList, "code", "asc", 0, 0);  
        },
        getMediaProperties: async function() {
            this.tooltips = await data.getMediaPropertiesByMediaType('ToolTip');
        },
        getLinkedSurveys: async function() {
            const urlParams = new URLSearchParams(window.location.search);
            return await data.browseSurveysByQuestionId(urlParams.get('ID'));
        },
        saveQuestion: async function(fieldName, printMessages = true) {
            if(!this.question.questionType) 
                alert('Question Type must be set in order to save');
            else {
                let obj = {};
                if(fieldName) {
                    if(fieldName === 'displayParameters') {
                        if(!this.question.displayParameters.numberStep)
                            this.question.displayParameters.numberStep = 1;
                        if(this.hasPlaceholder(this.question)) {
                            this.question.displayParameters.placeholderJson = await translation.addTranslations(this.question.displayParameters.placeholderJson, this.question.displayParameters.placeholder, this.question.languages, this.question.defaultLanguage);
                        }
                    }
                    if(fieldName === 'questionTextJson' || fieldName === 'moreInfoJson') {
                        this.question[fieldName] = await translation.addTranslations(this.question[fieldName], this[fieldName.replace('Json','')], this.question.languages, this.question.defaultLanguage);
                    }

                    if(fieldName === 'mediaPropertyId') {
                        translation.translateMediaProperty(this.question.mediaPropertyId, this.question.languages);
                    }
                    obj.id = this.question.id;
                    obj[fieldName] = this.question[fieldName];
                    obj.questionType = this.question.questionType;
                } else {
                    obj = this.question;
                }
                const resp = await data.saveQuestion(obj);
                if(printMessages)
                    this.processSaveResult(resp);
            }
        },
        reactivateQuestion: async function() {
            this.question.isActive = true;
            this.processReactivateResult(await data.saveQuestion(this.question));
        },
        deactivateQuestion: async function() {
            if(confirm('Are you sure you want to deactivate this question?')) {
                this.question.isActive = false;
                this.processDeactivateResult(await data.saveQuestion(this.question));
            }
        },
        saveComboAnswer: async function(sequence) {
            var filteredAnswers = this.question.answers.filter(search => search.sequence == sequence);
            var obj = {
                    sequence: sequence,
                };

            var replacementFields = [];

            if(filteredAnswers.length > 0)
                obj = filteredAnswers[0];

            if(sequence == 1) {
                obj.answerText = this.ComboQuestionAnswers.textboxLabel;
                obj.answerValue = "XX";

                replacementFields.push( { 
                    searchString: "AnswerText",
                    replaceString: "Textbox Label"
                });
            }
            else if(sequence == 2) {
                obj.answerText = this.ComboQuestionAnswers.buttonText;
                obj.answerValue = this.ComboQuestionAnswers.buttonValue;

                replacementFields.push( { 
                    searchString: "AnswerText",
                    replaceString: "Button Text"
                });

                replacementFields.push( { 
                    searchString: "AnswerValue",
                    replaceString: "Button Value"
                });
            }
            else 
                return;

            var ret = this.processSaveResult(await data.saveAnswer(this.question.id, obj), replacementFields);
            return ret;
        },
        saveAnswer: async function(ans, fieldName, printMessages = true) {
            let obj = {};
            if(fieldName) {
                if(fieldName === 'answerText') {
                    ans.answerTextJson = await translation.addTranslations(ans.answerTextJson, ans.answerText, this.question.languages, this.question.defaultLanguage);
                    await this.saveAnswer(ans, 'answerTextJson', false);
                }
                obj.id = ans.id;
                obj[fieldName] = ans[fieldName];
            } else {
                if(!ans.id || !ans.answerTextJson || ans.answerTextJson.length !== this.question.languages.length) {
                    ans.answerTextJson = await translation.addTranslations(ans.answerTextJson, ans.answerText, this.question.languages, this.question.defaultLanguage);
                }
                obj = ans;
            }

            var ret = await data.saveAnswer(this.question.id, obj);
            if(printMessages) {
                this.processSaveResult(ret);
                this.sortAnswers();            
            }           
            
            return ret;
        },
        deleteAnswer: async function(ans) {
            let confirmStr = '';
            if(this.isSelectQuestion)
                confirmStr = 'Are you sure you want to delete this answer? There will be no way to recover it.';
            else if(this.isValueSelectQuestion)
                confirmStr = 'Are you sure you want to delete this label? There will be no way to recover it.';
            
            if(confirm(confirmStr)){
                var result = this.processDeleteResult(await data.deleteAnswer(this.question.id, ans.id));
                if(result.success) {
                    var index = this.question.answers.findIndex(search => search.id == ans.id);
                    this.question.answers[index].isActive = false;
                  //  this.answers.splice(index, 1);

                }
            }
        },
        undeleteAnswer: async function(ans) {
            ans.isActive = true;
            this.saveAnswer(ans, 'isActive');
        },
        goToList: function() {
            window.location.href = '/questions';
        },
        goToSurvey: function() {
            window.location.href = this.surveyUrl;
        },
        sortAnswers: async function() {
            this.question.answers.sort((a,b) => {
                return a.sequence - b.sequence;
            });
        },
        showAddMediaPropertyModal: function() {
            this.showAddVideoModal = true;
        },
        hideAddMediaPropertyModal: function() {
            this.showAddVideoModal = false;
        },
        saveMediaProperty: function(question, mp) {
            question.mediaPropertyId = mp.id;
            if(mp.mediaType === 'Video') {
                question.answerType = 'Video';
                question.mediaPropertyInfo = mp;
            }
            else if(mp.mediaType === 'Image') {
                question.answerType = 'Answer';
                question.mediaPropertyInfo = mp;
            }

            this.saveQuestion('mediaPropertyId');
        },
        removeMediaProperty: async function() {
            this.question.mediaPropertyId = 0;
            this.question.mediaPropertyInfo = null;
            this.saveQuestion('mediaPropertyId');
        },
        hasPlaceholder: function(question) {
            return this.placeholderTypes.indexOf(question.questionType) >= 0;
        },
        toggleParam: function(e, param) {
            if(!e.target.checked) {
                this.question.displayParameters[param] = null;
                this.saveQuestion('displayParameters');
            }
        },
        getPrintConditions: function(conditions) {
            if(conditions != undefined && conditions != '{}' && conditions != '')
                return printConditions(JSON.parse(conditions));
            else 
                return '';
        },
        showConditionalModal: function(object, type) {
            if(object.conditionalJson == null || object.conditionalJson == undefined)
                this.conditionGroup = {};
            else 
                this.conditionGroup = JSON.parse(object.conditionalJson);

            this.conditionalExpressionObject = object;
            this.ExpressionModalVisible = true;
            this.ExpressionObjectType = type;
        },
        updateConditions: function(conditionGroup) {
            this.conditionalExpressionObject.conditionalJson =  JSON.stringify(conditionGroup);
            this.saveAnswer(this.conditionalExpressionObject, 'conditionalJson');
        },
        moveAnswer: async  function() {
            var result; 

            for(var i = 0; i < this.question.answers.length; i++) {
                if(this.question.answers[i].sequence != i + 1) {
                    this.question.answers[i].sequence = i+1;
                    result =  await this.saveAnswer(this.question.answers[i], 'sequence', false);
                    if(!result.success) {
                        this.processSaveResult();
                        this.sortAnswers();
                        break;
                    }                    
                }
            }

            if(result.success) {
                this.processSaveResult(result);
            }
        },
        addLanguage: async function(language) {
            this.question.languages.push(language);
            await translation.addTranslationToField(this.question.questionTextJson, language, this.question.defaultLanguage, true);
            await this.saveQuestion('questionTextJson', false);
            if(this.question.moreInfo) {
                await translation.addTranslationToField(this.question.moreInfoJson, language, this.question.defaultLanguage, true);
                await this.saveQuestion('moreInfoJson', false);
            }
            if(this.question.answers) {
                for(let i = 0; i < this.question.answers.length; i++) {
                    await translation.addTranslationToField(this.question.answers[i].answerTextJson, language, this.question.defaultLanguage, true);
                    await this.saveAnswer(this.question.answers[i], 'answerTextJson', false);
                }
            }
            if(this.hasPlaceholder(this.question)) {
                if(!this.question.displayParameters.placeholderJson)
                    this.question.displayParameters.placeholderJson = [];
                await translation.addTranslationToField(this.question.displayParameters.placeholderJson, language, this.question.defaultLanguage, true);
                await this.saveQuestion('displayParameters', false);
            }
            
            await this.saveQuestion('languages');

            translation.translateMediaProperty(this.question.mediaPropertyId, this.question.languages);
        },
        deleteLanguage: async function(lang) {
            const ind = this.question.languages.indexOf(lang)
            this.question.languages.splice(ind, 1);
            translation.removeLanguageFromField(this.question.questionTextJson, lang);
            await this.saveQuestion('questionTextJson', false);
            if(this.question.moreInfo) {
                translation.removeLanguageFromField(this.question.moreInfoJson, lang);
                await this.saveQuestion('moreInfoJson', false);
            }
            if(this.question.displayParameters.placeholder) {
                translation.removeLanguageFromField(this.question.displayParameters.placeholderJson, lang);
                await this.saveQuestion('displayParameters', false);
            }
            if(this.isSelectQuestion) {
                for(let i = 0; i < this.question.answers.length; i++) {
                    translation.removeLanguageFromField(this.question.answers[i].answerTextJson, lang);
                    await this.saveAnswer(this.question.answers[i], 'answerTextJson', false);
                }
            }
            await this.saveQuestion('languages');
        },
        changeDefaultLanguage: function(lang) {
            this.question.defaultLanguage = lang;
            this.saveQuestion('defaultLanguage');
        },
        getTextForLanguage(jsonField, language) {
            var field = jsonField.find((search) => search.language === language);
            return field.text != null ? field.text : '';
        }
    }
}
</script>

<style scoped>
    .content {
        display: flex;
        text-align: left;
    }
    .backToSurvey {
        margin-top: 20px;
    }
    .questionContainer {
        flex: 1 1 100px;
        max-width: 400px;
        padding: 0 40px 0 50px;
        position: relative;
    }
    .questionContainer textarea {
        width: 100%;
        min-height: 80px;
    }
    .questionContainer h4 {
        display: inline-block;
    }
    .questionContainer select {
        display:block;
    }
    .answersContainer {
        flex: 3 1 600px;
        border-left: 1px solid lightgray;
        padding-left: 50px;
    }
    .reactivateButton {
        height: 40px;
        width: 200px;
        border-radius: 5px;
        background-color: lightgreen;
        border-color: lightgreen;
        font-size: 16px;
        cursor: pointer;
        margin-bottom: 10px;
    }
    .deactivateButton {
        height: 40px;
        width: 200px;
        border-radius: 5px;
        background-color: darkred;
        border-color: darkred;
        color: white;
        font-size: 16px;
        cursor: pointer;
        margin: 20px 0 10px 0;
    }
    .deleteButton {
        position: absolute;
        height: 40px;
        width: 200px;
        font-size: 16px;
        background-color: darkred;
        border-color: darkred;
        color: white;
        border-radius: 10px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        bottom: 10px;
        right: 10px;
    }
    .deleteButton:disabled {
        color: gray;
    }
    .answer {
        display: flex;
        position: relative;
        background-color: #e7e7e7;
        border-radius: 10px;
        margin: 20px 0;
        padding: 10px;
    }
    .answerText {
        flex: 1 1 100px;
        padding: 20px;
    }
    .answerText input, .answerValue input {
        width: 100%;
    }
    .answerValue {
        flex: 1 1 100px;
        padding: 20px;
    }

    .openModalButton {
        display: inline-block;
        background-color: unset;
        border: none;
        cursor: pointer;
        font-size: 12px;
        color:#036991;
    }
    .removeMediaPropertyButton {
        display: inline-block;
        background-color: unset;
        border: none;
        cursor: pointer;
        font-size: 12px;
        color:darkred;
    }

    .dataRow {
        display: flex;
        min-height: 40px;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    }

    .dragArea {
        display: flex; 
        flex-direction: row;
        flex-wrap: wrap;
    }

    :deep(.conditionalJson) {
        width: 100%;
    }

    :deep(.conditionalJson textarea) {
        width: 95%;
        min-height: 100px;
    }   

    :deep(.conditionalJson) {
        flex: 1 1 400px;
        display: flex;
        flex-direction: row; 
        align-items: center;
    }


    :deep(.conditionalJson div) {
        display: inline;
    }

    .row-header {
        font-weight: bold;
        border-bottom-style: double;
        text-align: center;  
    }
    
    .row-header>div {
        justify-content: center;
    }

    .row-data {
        border-bottom: solid 1px;
    }
    
    .row-data:nth-child(odd) {
        background-color: lightgray;
    }
    .row {
        display: flex;
        flex-direction: row;
        position: relative;
        width: 100%;
        padding: 10px 0px;

        & > div {
            margin: 0px 10px 0px 0px;
            box-sizing: border-box;
            border-right: solid 1px;
            padding-right: 10px;
        }

        & > div:first-child {
            border-right: none;
        }

        .grip {
            flex: 0 0 20px;
            cursor: grab;
        }

        .editConditions { 
            display: inline;
            background-color: unset;
            border: none;
            cursor: pointer;
            color: #036991;
            line-height: unset;
            margin-right: 10px;
        }

        .button-column {
            flex: 0 0 30px;
            display: flex; 
            flex-direction: row; 
            align-items: center; 
            border-right: none;

            & > div {
                cursor: pointer; 
            }
        }

        .delete-button-column {
            & > div {
                color: darkred;
            }
        }

        .undelete-button-column {
            & > div {
                color: green;
            }
        }

        & a {
            text-decoration: none;
            color: #2c3e50;
        }

        .column-1x {
            flex: 0 0 200px;
        }

        .column-2x {
            flex: 0 0 400px;

        }

    }

</style>